import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Download, Users, Crown, Phone, Search, Upload, FileText, CheckCircle2, AlertCircle } from 'lucide-react';

export default function AdminCustomers() {
  const { getHeaders, API } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outlets, setOutlets] = useState([]);
  const [filterOutlet, setFilterOutlet] = useState('all');
  const [filterMember, setFilterMember] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/outlets`, { headers: getHeaders() })
      .then(res => setOutlets(res.data))
      .catch(() => {});
  }, [API, getHeaders]);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/customers?`;
      if (filterOutlet !== 'all') url += `outlet_id=${filterOutlet}&`;
      if (filterMember !== 'all') url += `member_filter=${filterMember}&`;
      if (dateFrom) url += `date_from=${dateFrom}&`;
      if (dateTo) url += `date_to=${dateTo}&`;
      const res = await axios.get(url, { headers: getHeaders() });
      setCustomers(res.data);
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  }, [API, getHeaders, filterOutlet, filterMember, dateFrom, dateTo]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const filtered = search
    ? customers.filter(c =>
        c.guest_name.toLowerCase().includes(search.toLowerCase()) ||
        c.mobile.includes(search) ||
        (c.member_id && c.member_id.toLowerCase().includes(search.toLowerCase()))
      )
    : customers;

  const exportExcel = () => {
    if (!filtered.length) { toast.error('No data to export'); return; }
    const rows = filtered.map(c => ({
      'Mobile Number': c.mobile,
      'Guest Name': c.guest_name,
      'Member': c.is_member ? 'Yes' : 'No',
      'Member ID': c.member_id || '',
      'Total Visits': c.total_visits,
      'Last Visit Date': c.last_visit_date,
      'Total Reservations': c.total_reservations,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 15 }, { wch: 22 }, { wch: 10 }, { wch: 14 },
      { wch: 12 }, { wch: 16 }, { wch: 18 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Customer Database');
    XLSX.writeFile(wb, `Customer_Database_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Exported to Excel');
  };

  const totalMembers = customers.filter(c => c.is_member).length;

  // ── Bulk-upload customers ──────────────────────────
  const fileInputRef = useRef(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers/template`, { headers: getHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'customers-template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleUploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/customers/upload`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      if (res.data.success > 0) toast.success(`Imported ${res.data.success} customer${res.data.success === 1 ? '' : 's'}`);
      await loadCustomers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="admin-customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-[#1A2421]">Customer Database</h2>
          <p className="text-sm text-[#5C6B64] mt-1">Unique guests across all outlets</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden"
            onChange={(e) => { handleUploadFile(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="upload-customers-input"
          />
          <Button size="sm" variant="outline" onClick={downloadTemplate}
            className="h-8 text-xs gap-1" data-testid="download-customer-template-btn">
            <FileText className="w-3.5 h-3.5" /> Template
          </Button>
          <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="h-8 text-xs gap-1" data-testid="upload-customers-btn">
            <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploading…' : 'Upload Customers'}
          </Button>
          <Button size="sm" onClick={exportExcel}
            className="h-8 text-xs bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white gap-1" data-testid="export-customers-btn">
            <Download className="w-3.5 h-3.5" /> Export to Excel
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Card className="border-[#E2E8DF] shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-[#5C6B64] uppercase">Total Customers</p>
            <p className="text-xl font-bold text-[#1A2421]" data-testid="total-customers">{customers.length}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8DF] shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-[#5C6B64] uppercase">Members</p>
            <p className="text-xl font-bold text-[#D4A017]" data-testid="total-members">{totalMembers}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8DF] shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-[#5C6B64] uppercase">Non-Members</p>
            <p className="text-xl font-bold text-[#5C6B64]">{customers.length - totalMembers}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8DF] shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-[#5C6B64] uppercase">Repeat Guests</p>
            <p className="text-xl font-bold text-[#2D6A4F]">{customers.filter(c => c.total_visits > 1).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-[#5C6B64]" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name, mobile, member ID"
            className="h-8 pl-8 text-xs border-[#E2E8DF]" data-testid="customer-search" />
        </div>
        <Select value={filterOutlet} onValueChange={setFilterOutlet}>
          <SelectTrigger className="w-36 h-8 border-[#E2E8DF] text-xs" data-testid="customer-outlet-filter">
            <SelectValue placeholder="All Outlets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Outlets</SelectItem>
            {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMember} onValueChange={setFilterMember}>
          <SelectTrigger className="w-32 h-8 border-[#E2E8DF] text-xs" data-testid="customer-member-filter">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="members">Members</SelectItem>
            <SelectItem value="non-members">Non-Members</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="h-8 w-36 text-xs border-[#E2E8DF]" data-testid="customer-date-from" />
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="h-8 w-36 text-xs border-[#E2E8DF]" data-testid="customer-date-to" />
        <Badge variant="outline" className="border-[#E2E8DF] text-[#5C6B64] text-xs ml-auto">
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
        </div>
      ) : (
        <Card className="border-[#E2E8DF] shadow-sm overflow-hidden">
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <p className="text-sm text-[#5C6B64] text-center py-10">No customers found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-[#1A2421]">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.05em] text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                      <th className="px-4 py-2.5 font-medium">Mobile</th>
                      <th className="px-4 py-2.5 font-medium">Guest Name</th>
                      <th className="px-4 py-2.5 font-medium text-center">Member</th>
                      <th className="px-4 py-2.5 font-medium">Member ID</th>
                      <th className="px-4 py-2.5 font-medium text-center">Total Visits</th>
                      <th className="px-4 py-2.5 font-medium">Last Visit</th>
                      <th className="px-4 py-2.5 font-medium text-center">Reservations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, i) => (
                      <tr key={c.mobile}
                        className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}
                        data-testid={`customer-row-${c.mobile}`}>
                        <td className="px-4 py-2.5 font-medium whitespace-nowrap">
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3 text-[#5C6B64]" />{c.mobile}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">{c.guest_name || '—'}</td>
                        <td className="px-4 py-2.5 text-center">
                          {c.is_member ? (
                            <Badge className="text-[10px] bg-[#D4A017]/10 text-[#D4A017] border-[#D4A017]/20">
                              <Crown className="w-2.5 h-2.5 mr-0.5" />Yes
                            </Badge>
                          ) : (
                            <span className="text-xs text-[#5C6B64]">No</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-[#5C6B64]">{c.member_id || '—'}</td>
                        <td className="px-4 py-2.5 text-center font-semibold">
                          <span className={c.total_visits > 1 ? 'text-[#2D6A4F]' : 'text-[#1A2421]'}>{c.total_visits}</span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-[#5C6B64] whitespace-nowrap">{c.last_visit_date || '—'}</td>
                        <td className="px-4 py-2.5 text-center">{c.total_reservations}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload result modal */}
      <Dialog open={!!uploadResult} onOpenChange={(o) => !o && setUploadResult(null)}>
        <DialogContent className="max-w-md" data-testid="upload-customers-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#2D6A4F]" />
              Customer Upload Complete
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5C6B64]">Summary of the import. Failed rows can be fixed and re-uploaded.</DialogDescription>
          </DialogHeader>
          {uploadResult && (
            <div className="space-y-3" data-testid="upload-customers-result-body">
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-md border border-[#E2E8DF] bg-[#F8FAF7]">
                  <div className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Total rows</div>
                  <div className="text-xl font-semibold text-[#1A2421] tabular-nums" data-testid="upload-result-total">{uploadResult.total_rows}</div>
                </div>
                <div className="p-3 rounded-md border border-[#2D6A4F]/30 bg-[#2D6A4F]/5">
                  <div className="text-[10px] uppercase tracking-wider text-[#2D6A4F]">Imported</div>
                  <div className="text-xl font-semibold text-[#2D6A4F] tabular-nums" data-testid="upload-result-success">{uploadResult.success}</div>
                </div>
                <div className="p-3 rounded-md border border-[#1F4D6B]/30 bg-[#1F4D6B]/5">
                  <div className="text-[10px] uppercase tracking-wider text-[#1F4D6B]">Duplicates</div>
                  <div className="text-xl font-semibold text-[#1F4D6B] tabular-nums" data-testid="upload-result-duplicates">{uploadResult.skipped_duplicates}</div>
                </div>
              </div>

              {uploadResult.failed?.length > 0 && (
                <div className="border border-[#C45A41]/30 bg-[#C45A41]/5 rounded-md p-3" data-testid="upload-result-failed-section">
                  <div className="flex items-center gap-1.5 mb-2 text-[#C45A41] text-xs font-semibold">
                    <AlertCircle className="w-3.5 h-3.5" /> {uploadResult.failed.length} row{uploadResult.failed.length === 1 ? '' : 's'} failed
                  </div>
                  <ul className="text-[11.5px] text-[#1A2421] space-y-1 max-h-48 overflow-y-auto">
                    {uploadResult.failed.slice(0, 30).map((f, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-mono text-[#5C6B64] w-12 shrink-0">Row {f.row}</span>
                        <span>{f.reason}</span>
                      </li>
                    ))}
                    {uploadResult.failed.length > 30 && (
                      <li className="text-[#5C6B64] italic">…and {uploadResult.failed.length - 30} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setUploadResult(null)} className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="upload-result-close">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
